import React, { useState, useRef, ReactNode, ReactElement } from 'react';
import Button from '@atlaskit/button';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { WidgetEmpty } from './widget-empty';
import { WidgetError } from './widget-error';
import './widget.scss';

export const scrollElement = ($el?: HTMLDivElement | null, config?: ScrollToOptions) => {
  if (!$el || !$el.scroll) {
    return;
  }
  // adding smooth scrolling to next page of the content
  setTimeout(() => {
    $el.scroll(config);
  }, 300);
};

/**
 * Widget Component to add inside the WidgetContainer component for each entity on the home page
 * @param props
 * @constructor
 */
export const Widget = ({
  // heading of the widget
  heading,
  // Adding all the children/widget items to be shown
  children,
  // count of items to show on first load before loading more
  initialItemsCount,
  // add HTML on the right of the heading of widget
  headingAfter,
  // tracking if the widget is loading the data
  loading,
  // tracking if the widget is having any error in loading data
  error,
  // test id to be added on root of the widget
  testId,
  // to represent the current state
  subHeading,
  // loader to be shown when loading
  loader,
  // class name to be added to each widget item. Can be used to add custom styles like background, etc.
  widgetItemClassName,
  // for rendering design iteration related components
  version,
}: {
  heading?: string;
  children?: ReactNode;
  initialItemsCount: number;
  headingAfter?: ReactNode;
  loading?: boolean;
  error?: any;
  testId?: string;
  subHeading?: ReactElement;
  loader?: ReactNode;
  widgetItemClassName?: string;
  version?: number;
}) => {
  const showLoadMore = React.Children.count(children) > initialItemsCount;
  const [isLoadMoreSelected, selectLoadMore] = useState<boolean>(false);
  const widgetContentRef = useRef<HTMLDivElement | null>(null);
  const getChildrenList = (children: ReactNode) => {
    return isLoadMoreSelected
      ? React.Children.toArray(children)
      : React.Children.toArray(children).slice(0, initialItemsCount);
  };
  const renderChildren = (children?: React.ReactNode) => {
    if (!children || React.Children.count(children) === 0) {
      return <WidgetEmpty version={version} />;
    }
    return React.Children.map(getChildrenList(children), (child, index) => (
      <div className={`${widgetItemClassName || ''} widget-item`} key={index}>
        {child}
      </div>
    ));
  };
  const onLoadMore = () => {
    selectLoadMore(true);
    scrollElement(widgetContentRef.current, {
      top: widgetContentRef.current?.offsetHeight || 300,
      behavior: 'smooth',
    });
  };

  const renderLoader = () => {
    return loader ? loader : <LoadingIndicator />;
  };

  const renderErrorOrChildren = () => {
    return error ? <WidgetError version={version} /> : renderChildren(children);
  };

  return (
    <div className="ppc-home-widget flex-column justify-space-between" data-testid={testId}>
      {!heading && !subHeading && !headingAfter ? null : (
        <div className="flex justify-space-between" data-testid="widget-header-container">
          {heading && <h3 className="widget-heading">{heading}</h3>}
          {subHeading && <p className="widget-content-sub-heading">{subHeading}</p>}
          {headingAfter}
        </div>
      )}
      <div className="ppc-widget-content" ref={widgetContentRef} data-testid={`${testId}-widget-content`}>
        {loading ? renderLoader() : renderErrorOrChildren()}
      </div>
      <div className="widget-footer widget-footer-v2">
        <div className="flex justify-center">
          {showLoadMore && !isLoadMoreSelected ? (
            <Button appearance="link" className="load-more" onClick={onLoadMore} testId={`${testId}-load-more`}>
              Load more
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
